/**
 * PricingDefaultsDialog — iter225
 * ───────────────────────────────
 * Per-workshop pricing defaults (labour rate $/h + default margin % on RRP
 * parts). These PREFILL new lines in every quote/invoice builder — always
 * editable per line, never applied to existing/quoted lines or templates.
 * Editable by Super Admin / Admin / Service Manager.
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Settings2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { clearPricingDefaultsCache } from '@/components/QuoteBuilder';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PricingDefaultsDialog({ user }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [labourRate, setLabourRate] = useState('');
  const [marginPct, setMarginPct] = useState('');
  const [orpMarginPct, setOrpMarginPct] = useState('');

  if (!user || !['super_admin', 'admin', 'service_manager'].includes(user.role)) return null;

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing-defaults`, { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setLabourRate(String(d.labour_rate ?? ''));
        setMarginPct(String(d.parts_margin_pct ?? ''));
        setOrpMarginPct(String(d.orp_margin_pct ?? ''));
      }
    } catch (_e) {
      toast.error('Could not load pricing defaults');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing-defaults`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          labour_rate: labourRate === '' ? null : Number(labourRate),
          parts_margin_pct: marginPct === '' ? null : Number(marginPct),
          orp_margin_pct: orpMarginPct === '' ? null : Number(orpMarginPct),
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Save failed');
      }
      clearPricingDefaultsCache();
      toast.success('Pricing defaults saved — new lines will prefill with these values');
      setOpen(false);
    } catch (e) {
      toast.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        onClick={openDialog}
        data-testid="pricing-defaults-button"
      >
        <Settings2 className="h-4 w-4 mr-2" />
        Pricing Defaults
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="pricing-defaults-dialog">
          <DialogHeader>
            <DialogTitle>Pricing Defaults</DialogTitle>
            <DialogDescription>
              Prefill values for NEW quote / invoice lines in every builder.
              Always editable per line. Existing lines, quotes and templates
              are never changed. Tyre margin stays manual.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-slate-600">Default labour rate ($/hour)</Label>
              <Input
                type="number" min="0" step="0.5"
                value={labourRate}
                onChange={(e) => setLabourRate(e.target.value)}
                disabled={loading}
                className="mt-1"
                data-testid="pricing-defaults-labour-rate"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Default margin % on RRP parts</Label>
              <Input
                type="number" min="0" step="1"
                value={marginPct}
                onChange={(e) => setMarginPct(e.target.value)}
                disabled={loading}
                className="mt-1"
                data-testid="pricing-defaults-margin"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600">ORP margin % (auto-fills RRP from ORP)</Label>
              <Input
                type="number" min="0" step="1"
                value={orpMarginPct}
                onChange={(e) => setOrpMarginPct(e.target.value)}
                disabled={loading}
                className="mt-1"
                data-testid="pricing-defaults-orp-margin"
              />
              <p className="text-[10px] text-slate-400 mt-1">
                e.g. 30 → ORP $10 auto-fills RRP $13. Only applies while typing a NEW ORP — saved lines never change.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving || loading} data-testid="pricing-defaults-save">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
